import {
  cloneSystemData,
  migrateSystemData,
  normalizeSystemDataContract,
  validateSystemData,
  validateSystemDataVersions
} from '../../plugin-contracts/system_data_contract.mjs';

const contracts = new Map();

function overlappingPaths(a, b) {
  return a.slice(0, Math.min(a.length, b.length)).every((key, index) => key === b[index]);
}

export function registerSystemDataContract(manifest, contract) {
  if (contracts.has(manifest?.systemId)) return false;
  const normalized = normalizeSystemDataContract(manifest, contract);
  if ([...contracts.values()].some(entry => overlappingPaths(entry.path, normalized.path))) return false;
  contracts.set(normalized.systemId, normalized);
  return true;
}

export function unregisterSystemDataContract(systemId) {
  return contracts.delete(systemId);
}

function dataAtPath(project, path) {
  let value = project;
  for (const key of path) {
    if (value === null || typeof value !== 'object' || !Object.hasOwn(value, key)) return { present: false };
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    if (!('value' in descriptor)) {
      throw Object.assign(new TypeError('Systemdatenpfade dürfen keine Getter enthalten.'), { code: 'INVALID_SYSTEM_DATA' });
    }
    value = descriptor.value;
  }
  return { present: true, value };
}

function versionFor(project, contract) {
  const sourceVersion = project.systemDataVersions?.[contract.systemId] ?? 0;
  if (sourceVersion > contract.version) {
    throw Object.assign(new TypeError(`${contract.systemId}: Datenversion ${sourceVersion} wird noch nicht unterstützt.`), {
      code: 'UNSUPPORTED_SYSTEM_DATA_VERSION'
    });
  }
  return sourceVersion;
}

function prepareOrderData(project, migrate = false) {
  if (!Object.hasOwn(project, 'systemOrderData')) return undefined;
  const store = cloneSystemData(dataAtPath(project, ['systemOrderData']).value);
  if (!store || typeof store !== 'object' || Array.isArray(store)) {
    throw Object.assign(new TypeError('Ungültiger Auftragsdatenbereich.'), { code: 'INVALID_SYSTEM_DATA' });
  }
  for (const [owner, entry] of Object.entries(store)) {
    if (!owner.trim() || !entry || typeof entry !== 'object' || Array.isArray(entry)
      || !Number.isSafeInteger(entry.version) || entry.version < 0 || !Object.hasOwn(entry, 'data')) {
      throw Object.assign(new TypeError('Ungültiger Auftragsdatenvertrag.'), { code: 'INVALID_SYSTEM_DATA' });
    }
    const contract = contracts.get(owner)?.orderData;
    if (!contract) continue; // Unknown plugin values remain opaque, strict JSON.
    validateSystemData(contract, entry.data, entry.version);
    if (migrate && entry.version < contract.version) {
      store[owner] = { ...entry, ...migrateSystemData(contract, entry.data, entry.version) };
    }
  }
  return store;
}

export function validateProjectSystemData(project) {
  validateSystemDataVersions(project);
  prepareOrderData(project);
  for (const contract of contracts.values()) {
    const sourceVersion = versionFor(project, contract);
    const data = dataAtPath(project, contract.path);
    if (data.present) validateSystemData(contract, data.value, sourceVersion);
  }
  return true;
}

export function projectSystemDataNeedsMigration(project) {
  validateProjectSystemData(project);
  return [...contracts.values()].some(contract => (
    (contract.orderData && project.systemOrderData?.[contract.systemId]?.version < contract.orderData.version)
    || dataAtPath(project, contract.path).present && versionFor(project, contract) < contract.version
  ));
}

// Clone only path ancestors: a failed owner never leaves half-migrated data, and
// unaffected runtime/opaque values keep their existing isolation and identity.
function withDataAtPath(project, path, data) {
  const root = { ...project };
  let next = root;
  let previous = project;
  path.forEach((key, index) => {
    if (index === path.length - 1) {
      next[key] = data;
    } else {
      const previousChild = previous?.[key];
      if (previousChild !== undefined && (previousChild === null || typeof previousChild !== 'object' || Array.isArray(previousChild))) {
        throw new TypeError('Systemdatenpfad kann keinen bestehenden fremden Wert ersetzen.');
      }
      next[key] = { ...(previousChild || {}) };
      next = next[key];
      previous = previousChild;
    }
  });
  return root;
}

/** Mutates only after every registered owner has successfully prepared its data. */
export function migrateProjectSystemData(project, { initialize = false } = {}) {
  validateSystemDataVersions(project);
  const orderData = prepareOrderData(project, true);
  const orderChanged = JSON.stringify(orderData) !== JSON.stringify(project.systemOrderData);
  let prepared = orderChanged ? { ...project, systemOrderData: orderData } : project;
  let changed = orderChanged;
  const versions = { ...project.systemDataVersions };
  for (const contract of contracts.values()) {
    const sourceVersion = versionFor(project, contract);
    const data = dataAtPath(project, contract.path);
    if (!data.present && !initialize) continue;
    if (data.present && sourceVersion === contract.version) {
      validateSystemData(contract, data.value, sourceVersion);
      continue;
    }
    const migrated = data.present
      ? migrateSystemData(contract, data.value, sourceVersion).data
      : cloneSystemData(contract.defaults);
    prepared = withDataAtPath(prepared, contract.path, migrated);
    versions[contract.systemId] = contract.version;
    changed = true;
  }
  if (changed) {
    Object.assign(project, prepared);
    if (Object.keys(versions).length || Object.hasOwn(project, 'systemDataVersions')) project.systemDataVersions = versions;
  }
  return project;
}
